import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ref, onValue, set, remove, get } from 'firebase/database';
import { ArrowLeft, Check, ExternalLink, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import WrittenTraining from '../components/WrittenTraining';
import { writtenTrainings } from '../data/writtenTrainings';
import { TRAINING_MODULES as MODULES, resumeIndex } from '../data/trainingModules';
import { isLimitedAgent } from '../config/roleAccess';
import { limitedModule, limitedWritten } from '../data/limitedAgentTraining';

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// Turns a YouTube video or playlist link into an embeddable player URL. Returns null for other links.
const youtubeEmbedUrl = (url) => {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'drive.google.com') {
      const m = u.pathname.match(/\/file\/d\/([^/]+)/);
      return m ? `https://drive.google.com/file/d/${m[1]}/preview` : null;
    }
    let id = '';
    if (host === 'youtu.be') id = u.pathname.slice(1);
    else if (host === 'youtube.com' && u.pathname === '/watch') id = u.searchParams.get('v') || '';
    const list = host === 'youtube.com' || host === 'youtu.be' ? u.searchParams.get('list') : null;
    if (!id && !list) return null;
    const params = new URLSearchParams();
    if (list) params.set('list', list);
    if (u.searchParams.get('index')) params.set('index', u.searchParams.get('index'));
    const path = id ? `embed/${id}` : 'embed/videoseries';
    return `https://www.youtube-nocookie.com/${path}?${params.toString()}`;
  } catch (err) {
    return null;
  }
};



export default function Training({ moduleKey = 'agentSetup' }) {
  const { user, userRole } = useAuth();
  const limited = isLimitedAgent(userRole);
  const rawMod = MODULES[moduleKey] || MODULES.agentSetup;
  // Limited Agents see training with nothing about Resi; hidden modules send them back to Training.
  const limitedMod = useMemo(() => (limited ? limitedModule(rawMod) : null), [limited, rawMod]);
  const blocked = limited && !limitedMod;
  const mod = limited ? (limitedMod || { ...rawMod, sections: [{ id: 'none', title: '', steps: [{ id: 'none', title: '', body: [] }] }] }) : rawMod;
  const allSteps = useMemo(
    () => mod.sections.flatMap((section) => section.steps.map((step) => ({ ...step, sectionId: section.id, sectionTitle: section.title }))),
    [mod]
  );
  const onboardingSections = mod.sections;
  const navigate = useNavigate();
  const uid = user?.uid;

  const [progress, setProgress] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Load this user's completed steps and where they left off. Re-runs when the module changes,
  // because React keeps this same component when moving from one training route to another.
  const [lastStepId, setLastStepId] = useState(null);
  const [positionLoaded, setPositionLoaded] = useState(false);
  useEffect(() => {
    setProgress({});
    setLoaded(false);
    setLastStepId(null);
    setPositionLoaded(false);
    if (!uid) return undefined;
    const unsubProgress = onValue(
      ref(database, `users/${uid}/training/${mod.id}`),
      (snapshot) => {
        setProgress(snapshot.val() || {});
        setLoaded(true);
      },
      (err) => {
        console.error('Training progress load failed:', err);
        setLoaded(true);
      }
    );
    get(ref(database, `users/${uid}/trainingPosition/${mod.id}`))
      .then((snap) => setLastStepId(snap.val()?.stepId || null))
      .catch((err) => console.error('Training position load failed:', err))
      .finally(() => setPositionLoaded(true));
    return () => unsubProgress();
  }, [uid, mod.id]);

  // Once both are loaded for this module, reopen the step they were on (or the first unfinished one)
  const [resumedFor, setResumedFor] = useState(null);
  useEffect(() => {
    if (!loaded || !positionLoaded || resumedFor === mod.id) return;
    setIndex(resumeIndex(allSteps, progress, lastStepId));
    setResumedFor(mod.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, positionLoaded, mod.id]);

  // Remember the open step so coming back (on any device) returns here
  useEffect(() => {
    if (!uid || resumedFor !== mod.id) return;
    const s = allSteps[index];
    if (!s) return;
    set(ref(database, `users/${uid}/trainingPosition/${mod.id}`), { stepId: s.id, updatedAt: new Date().toISOString() })
      .catch((err) => console.error('Training position save failed:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, index, resumedFor, mod.id]);

  const step = resumedFor === mod.id ? allSteps[index] : null;
  const written = step ? (limited ? limitedWritten(writtenTrainings[step.id]) : writtenTrainings[step.id]) : null;
  const completedCount = useMemo(
    () => allSteps.filter((s) => progress[s.id]).length,
    [progress, allSteps]
  );
  const percent = Math.round((completedCount / allSteps.length) * 100);
  const isDone = !!progress[step?.id];
  const needsAck = !!(step?.requireAck || step?.requireQuiz) && !isDone;
  const isLast = index === allSteps.length - 1;
  const videoLinks = (step?.links || []).filter((l) => youtubeEmbedUrl(l.url));
  const otherLinks = (step?.links || []).filter((l) => !youtubeEmbedUrl(l.url));

  const writeProgress = async (stepId, completed) => {
    if (!uid) return;
    setSaveError('');
    const stepRef = ref(database, `users/${uid}/training/${mod.id}/${stepId}`);
    try {
      if (completed) {
        await set(stepRef, { completedAt: new Date().toISOString() });
      } else {
        await remove(stepRef);
      }
    } catch (err) {
      console.error('Training progress save failed:', err);
      setSaveError('Could not save your progress. Check your connection and try again.');
    }
  };

  const handleNext = async () => {
    if (!step) return;
    if (needsAck) return;
    if (!isDone) await writeProgress(step.id, true);
    if (!isLast) setIndex(index + 1);
  };

  const handleUndo = async () => {
    if (!step || !isDone) return;
    await writeProgress(step.id, false);
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(step.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setCopied(false);
    }
  };

  let runningIndex = 0;

  if (blocked) return <Navigate to="/training" replace />;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button onClick={() => navigate('/training')} style={styles.backButton}>
          <ArrowLeft size={16} /> Back to training
        </button>
        <div style={styles.moduleTitle}>{mod.title}</div>
      </div>

      <div style={styles.progressWrap}>
        <div style={styles.progressText}>
          {completedCount} of {allSteps.length} steps complete ({percent}%)
        </div>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${percent}%` }} />
        </div>
      </div>

      <div style={styles.layout}>
        {/* Outline */}
        <aside style={styles.outline}>
          {[[mod.title, onboardingSections]].map(([groupName, list]) => (
          <div key={groupName}>
          <div style={styles.groupTitle}>{groupName}</div>
          {list.map((section) => (
            <div key={section.id} style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>{section.title}</div>
              {section.steps.map((s) => {
                const stepIndex = runningIndex++;
                const active = stepIndex === index;
                const done = !!progress[s.id];
                return (
                  <button
                    key={s.id}
                    onClick={() => setIndex(stepIndex)}
                    style={{
                      ...styles.outlineItem,
                      ...(active ? styles.outlineItemActive : {}),
                    }}
                  >
                    <span
                      style={{
                        ...styles.outlineCheck,
                        ...(done ? styles.outlineCheckDone : {}),
                      }}
                    >
                      {done ? <Check size={12} /> : null}
                    </span>
                    <span>{s.title}</span>
                  </button>
                );
              })}
            </div>
          ))}
          </div>
          ))}
        </aside>

        {/* Step content */}
        <main style={styles.main}>
          {step && (
            <>
              <div style={styles.sectionLabel}>{step.sectionTitle}</div>
              <h2 style={styles.stepTitle}>{step.title}</h2>

              {step.videoPlaceholder && (
                <div style={styles.videoBox}>
                  <div style={styles.videoPlay}>▶</div>
                  <div style={styles.videoText}>{step.videoPlaceholder}</div>
                </div>
              )}

              {written && <WrittenTraining training={written} />}
              {!written && step.body.map((para, i) => {
                const lines = String(para).split('\n');
                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    {lines.map((line, j) => {
                      const t = line.trim();
                      if (!t) return <div key={j} style={{ height: 6 }} />;
                      const img = t.match(/^\[\[IMG:([^|]+)\|(.*)\]\]$/);
                      if (img) {
                        return (
                          <figure key={j} style={{ margin: '12px 0 18px' }}>
                            <img src={img[1]} alt={img[2]} style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, border: '1px solid #d8c9a8' }} />
                          </figure>
                        );
                      }
                      if (t.startsWith('- ')) {
                        return (
                          <div key={j} style={{ ...styles.paragraph, paddingLeft: 20, position: 'relative', marginBottom: 6 }}>
                            <span style={{ position: 'absolute', left: 6 }}>•</span>
                            {t.slice(2)}
                          </div>
                        );
                      }
                      const isHeading = t.length <= 70 && !/[.?!:,"]$/.test(t) && !/^\d+\./.test(t);
                      if (isHeading) {
                        return (
                          <p key={j} style={{ ...styles.paragraph, fontSize: '20px', lineHeight: 1.35, fontWeight: 700, marginTop: 22, marginBottom: 8 }}>
                            {t}
                          </p>
                        );
                      }
                      return (
                        <p key={j} style={{ ...styles.paragraph, lineHeight: 1.65, marginBottom: 10 }}>
                          {t}
                        </p>
                      );
                    })}
                  </div>
                );
              })}

              {step.Component && <div style={{ marginBottom: 16 }}><step.Component /></div>}

              {step.requireAck && (
                <label data-testid="ack" style={styles.ackBox}>
                  <input type="checkbox" checked={isDone} onChange={(e) => writeProgress(step.id, e.target.checked)} style={{ width: 20, height: 20, marginRight: 10 }} />
                  I understand
                </label>
              )}

              {step.tipBox && (
                <div data-testid="tip-box" style={{ background: '#fbf7ee', border: '1px solid #e5d6b0', borderRadius: 8, padding: '12px 16px', margin: '4px 0 20px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8C6D1F', marginBottom: 6 }}>{step.tipBox.title}</div>
                  {step.tipBox.intro && <p style={{ margin: '0 0 6px', fontSize: 15, lineHeight: 1.55 }}>{step.tipBox.intro}</p>}
                  <ul style={{ paddingLeft: 20, margin: 0 }}>{(step.tipBox.items || []).map((t) => <li key={t} style={{ fontSize: 15, lineHeight: 1.55, marginBottom: 4 }}>{t}</li>)}</ul>
                </div>
              )}

              {step.images && step.images.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '8px 0 24px' }}>
                  {step.images.map((img) => (
                    <figure key={img.src} style={img.wide ? { margin: 0, flex: '1 1 100%', maxWidth: '100%' } : { margin: 0, flex: '1 1 200px', maxWidth: 260 }}>
                      <div style={img.wide ? { borderRadius: 8, overflow: 'hidden', border: '1px solid #d8c9a8' } : { background: '#1a1a1a', borderRadius: 8, padding: 16, border: '1px solid #d8c9a8' }}>
                        <img src={img.src} alt={img.alt} style={{ width: '100%', height: 'auto', display: 'block' }} />
                      </div>
                      <figcaption style={{ fontSize: 13, color: '#7f7f7f', marginTop: 8 }}>{img.label}</figcaption>
                    </figure>
                  ))}
                </div>
              )}

              {step.prompt && (
                <div style={styles.promptBox}>
                  <div style={styles.promptLabel}>ChatGPT prompt</div>
                  <div style={styles.promptText}>{step.prompt}</div>
                  <button onClick={copyPrompt} style={styles.copyButton}>
                    <Copy size={14} /> {copied ? 'Copied' : 'Copy prompt'}
                  </button>
                </div>
              )}

              {videoLinks.map((link) => (
                <div key={link.url} style={{ marginBottom: 20 }}>
                  <div style={styles.videoFrame}>
                    <iframe
                      src={youtubeEmbedUrl(link.url)}
                      title={link.label}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={styles.videoIframe}
                    />
                  </div>
                  <div style={styles.videoCaption}>{written ? `Optional: ${link.label}` : link.label}</div>
                </div>
              ))}

              {otherLinks.length > 0 && (
                <div style={styles.linkList}>
                  {otherLinks.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      {link.label} <ExternalLink size={13} />
                    </a>
                  ))}
                </div>
              )}

              {saveError && <div style={styles.error}>{saveError}</div>}

              {!step.hideFooter && <div style={styles.footer}>
                <button
                  onClick={handleUndo}
                  disabled={!isDone}
                  style={{ ...styles.undoButton, ...(!isDone ? styles.disabled : {}) }}
                >
                  Undo
                </button>
                <button onClick={handleNext} disabled={needsAck} style={{ ...styles.nextButton, ...(needsAck ? styles.disabled : {}) }}>
                  {isLast && isDone ? 'Module complete' : 'Next Step →'}
                </button>
              </div>}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f7f4ee',
    color: '#1a1a1a',
    fontFamily: FONT,
    padding: '20px 16px 40px',
    boxSizing: 'border-box',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: '1px solid #d8c9a8',
    borderRadius: '6px',
    padding: '8px 12px',
    cursor: 'pointer',
    color: '#1a1a1a',
    fontFamily: FONT,
    fontSize: '14px',
  },
  moduleTitle: {
    fontSize: '22px',
    fontWeight: 700,
  },
  progressWrap: {
    marginBottom: '20px',
  },
  progressText: {
    fontSize: '13px',
    color: '#6b6b6b',
    marginBottom: '6px',
  },
  progressTrack: {
    height: '8px',
    backgroundColor: '#e5e5e5',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#D4AF37',
    transition: 'width 0.3s ease',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)',
    gap: '20px',
    alignItems: 'start',
  },
  outline: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  liveCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    background: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '16px 20px',
    margin: '0 0 16px',
  },
  liveTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1a1a1a',
    fontFamily: FONT,
  },
  liveText: {
    fontSize: '13px',
    color: '#7f7f7f',
    marginTop: '4px',
    fontFamily: FONT,
  },
  liveButton: {
    padding: '12px 20px',
    background: 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)',
    color: '#1a1a1a',
    border: '1px solid #b8903a',
    borderRadius: '6px',
    fontWeight: 700,
    fontSize: '13px',
    textDecoration: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontFamily: FONT,
    whiteSpace: 'nowrap',
  },
  groupTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1a1a1a',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '10px 8px 6px',
    borderBottom: '2px solid #D4AF37',
    margin: '12px 0 10px',
  },
  sectionBlock: {
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#8C6D1F',
    fontWeight: 700,
    padding: '6px 8px',
  },
  outlineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    padding: '8px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#1a1a1a',
    fontFamily: FONT,
    fontSize: '14px',
  },
  outlineItemActive: {
    backgroundColor: '#f5efe1',
    fontWeight: 600,
  },
  outlineCheck: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '1px solid #c9c9c9',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  outlineCheckDone: {
    backgroundColor: '#1a1a1a',
    borderColor: '#1a1a1a',
    color: '#D4AF37',
  },
  main: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '24px',
    minWidth: 0,
  },
  sectionLabel: {
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#8C6D1F',
    fontWeight: 700,
    marginBottom: '6px',
  },
  stepTitle: {
    fontSize: '26px',
    margin: '0 0 16px',
    textWrap: 'balance',
  },
  videoBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    aspectRatio: '16 / 9',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
    color: '#D4AF37',
    textAlign: 'center',
    padding: '12px',
    boxSizing: 'border-box',
  },
  videoPlay: {
    fontSize: '32px',
  },
  videoText: {
    color: '#ffffff',
    fontSize: '14px',
  },
  paragraph: {
    fontSize: '17px',
    lineHeight: 1.6,
    margin: '0 0 14px',
  },
  promptBox: {
    backgroundColor: '#faf8f3',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  promptLabel: {
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#8C6D1F',
    fontWeight: 700,
    marginBottom: '8px',
  },
  promptText: {
    fontSize: '14px',
    lineHeight: 1.6,
    marginBottom: '12px',
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 14px',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: '14px',
  },
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  link: {
    color: '#8C6D1F',
    fontSize: '15px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    wordBreak: 'break-word',
  },
  error: {
    color: '#9b2c2c',
    fontSize: '14px',
    marginBottom: '12px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e5e5',
    flexWrap: 'wrap',
  },
  undoButton: {
    backgroundColor: 'transparent',
    color: '#1a1a1a',
    border: '1px solid #1a1a1a',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: '15px',
    fontWeight: 600,
  },
  nextButton: {
    backgroundColor: '#D4AF37',
    color: '#000000',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: '15px',
    fontWeight: 700,
  },
  videoFrame: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    background: '#1a1a1a',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  videoIframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
  },
  videoCaption: {
    fontSize: '14px',
    color: '#7f7f7f',
    marginTop: '6px',
    fontFamily: FONT,
  },
  ackBox: {
    display: 'flex',
    alignItems: 'center',
    background: '#fbf7ee',
    border: '2px solid #D4AF37',
    borderRadius: '8px',
    padding: '14px 16px',
    margin: '8px 0 16px',
    fontSize: '17px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
};
